import styled, { keyframes, css } from "styled-components";
import { COLORS } from "./colors";

// ═══════════════════════════════════════════════════════════════════════════════
// TypeWriterText_styles.js — Yoga Wellness Hero
// Design: Refined editorial | Sage-Forest brand palette from colors.js
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Keyframes ─────────────────────────────────────────────────────────────────

const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

const kFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const kBreathe = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(1);    }
  50%       { opacity: 1;   transform: scale(1.22); }
`;

const kRingPulse = keyframes`
  0%        { opacity: 0.3;  transform: scale(1);   }
  70%, 100% { opacity: 0;    transform: scale(2.4); }
`;

const kShimmer = keyframes`
  from { transform: translateX(-120%); }
  to   { transform: translateX(280%);  }
`;

const kCursorBlink = keyframes`
  0%,  49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const kChipReveal = keyframes`
  from { opacity: 0; transform: translateY(-6px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
`;

const kUnderlineGrow = keyframes`
  from { width: 0;    opacity: 0; }
  to   { width: 48px; opacity: 1; }
`;

const kTrustSlide = keyframes`
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0);    }
`;

const kStatPop = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.95); }
  to   { opacity: 1; transform: translateY(0)   scale(1);    }
`;

// ─── Motion helper ─────────────────────────────────────────────────────────────
const motion = (animCss) => css`
  @media (prefers-reduced-motion: no-preference) { ${animCss} }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// HERO ROOT — tokens derived entirely from colors.js
// ═══════════════════════════════════════════════════════════════════════════════

export const HeroRoot = styled.div`
  /* ── Brand palette tokens (from colors.js) ── */
  --c-primary:       ${COLORS.primary};          /* Sage Forest  */
  --c-primary-light: ${COLORS.primaryLight};     /* Eucalyptus   */
  --c-primary-dark:  ${COLORS.primaryDark};      /* Deep Forest  */
  --c-accent:        ${COLORS.accent};           /* Warm Sand    */
  --c-accent-dark:   ${COLORS.accentDark};       /* Terracotta   */

  --c-ink:           ${COLORS.textDark};         /* Deep Forest  */
  --c-ink-mid:       ${COLORS.textBody};         /* Bark Brown   */
  --c-ink-soft:      ${COLORS.textMid};          /* Stone Sage   */

  --c-surface:       ${COLORS.surface82};
  --c-surface-hi:    ${COLORS.surface98};
  --c-border:        ${COLORS.border};
  --c-border-accent: ${COLORS.borderGreen20};
  --c-tint:          ${COLORS.primaryTint};
  --c-glow:          ${COLORS.primaryGlow};
  --c-shadow:        ${COLORS.primaryShadow};

  /* Typewriter word colours — on-brand, not generic */
  --tw-1: ${COLORS.primary};        /* Sage Forest  */
  --tw-2: ${COLORS.accent};         /* Warm Sand    */
  --tw-3: ${COLORS.accentDark};     /* Terracotta   */

  /* ── Radii ── */
  --r-sm:   0.5rem;
  --r:      0.875rem;
  --r-lg:   1.25rem;
  --r-pill: 999px;

  /* ── Shadows ── */
  --s-xs: 0 1px 4px  ${COLORS.primaryShadow};
  --s-sm: 0 2px 12px ${COLORS.primaryShadow};
  --s:    0 4px 24px ${COLORS.primaryShadow};
  --s-lg: 0 8px 40px ${COLORS.primaryGlow};

  /* ── Spacing ── */
  --sp-2:  0.5rem;
  --sp-3:  0.75rem;
  --sp-4:  1rem;
  --sp-5:  1.25rem;
  --sp-6:  1.5rem;
  --sp-8:  2rem;

  /* ── Transitions ── */
  --t:      0.2s ease;
  --t-slow: 0.36s cubic-bezier(0.22, 1, 0.36, 1);

  /* ── Layout ── */
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  /* Top padding creates breathing room below parent navbar/chip overlap */
  padding-top: 0.5rem;
  z-index: 2;

  @media (max-width: 48em) {
    align-items: center;
    padding-top: 0.25rem;
  }
`;
HeroRoot.displayName = "HeroRoot";

// ═══════════════════════════════════════════════════════════════════════════════
// EYEBROW CHIP — "Online Yoga Classes · Now Live"
// Sits at top of the text column, separated from any parent elements by margin
// ═══════════════════════════════════════════════════════════════════════════════

export const EyebrowChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.85rem 0.3rem 0.5rem;
  border-radius: var(--r-pill);
  border: 1px solid var(--c-border-accent);
  background: ${COLORS.bgLight};           /* Pale Jade — soft, on-brand */
  /* Clear separation from whatever lives above this component */
  margin-bottom: var(--sp-5);
  align-self: flex-start;
  ${motion(css`animation: ${kChipReveal} 0.45s 0.05s cubic-bezier(0.22,1,0.36,1) both;`)}

  .chip-dot-wrap {
    position: relative;
    width: 9px;
    height: 9px;
    flex-shrink: 0;
  }
  .chip-dot {
    position: absolute;
    inset: 1px;
    border-radius: 50%;
    background: var(--c-primary);
    ${motion(css`animation: ${kBreathe} 2.2s ease-in-out infinite;`)}
  }
  .chip-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: var(--c-primary);
    ${motion(css`animation: ${kRingPulse} 1.7s 0.3s ease-out infinite;`)}
  }
  .chip-label {
    font-family: "DM Mono", "Courier New", monospace;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.17em;
    text-transform: uppercase;
    color: var(--c-primary-dark);
    white-space: nowrap;
  }

  @media (max-width: 48em) {
    align-self: center;
  }
`;
EyebrowChip.displayName = "EyebrowChip";

// ═══════════════════════════════════════════════════════════════════════════════
// HEADLINE — tightened sizing, brand text colours
// ═══════════════════════════════════════════════════════════════════════════════

export const StaticTitle = styled.h1`
  font-family: "Cormorant Garamond", "Playfair Display", Georgia, serif;
  /* ↓ Reduced from 3.8rem max — more proportional in layout */
  font-size: clamp(1.4rem, 2.8vw, 2.1rem);
  font-weight: 400;
  line-height: 1;
  letter-spacing: -0.01em;
  color: var(--c-ink);
  margin: 0;
  width: 100%;
  ${motion(css`animation: ${kFadeUp} 0.55s 0.12s cubic-bezier(0.22,1,0.36,1) both;`)}

  em {
    font-style: italic;
    color: var(--c-primary);           /* Sage Forest — stronger brand signal */
  }

  @media (max-width: 48em) {
    text-align: center;
    font-size: clamp(1.7rem, 6.5vw, 2.4rem);
  }
  @media (max-width: 30em) {
    font-size: clamp(1.5rem, 6vw, 2rem);
  }
`;
StaticTitle.displayName = "StaticTitle";

// ═══════════════════════════════════════════════════════════════════════════════
// TYPEWRITER LINE — same size family as StaticTitle, italic
// ═══════════════════════════════════════════════════════════════════════════════

export const TypeLine = styled.div`
  font-family: "Cormorant Garamond", "Playfair Display", Georgia, serif;
  font-size: clamp(1.1rem, 2.8vw, 2.1rem);
  font-weight: 700;
  font-style: italic;
  line-height: 1.1;
  letter-spacing: -0.02em;
  /* Fixed height prevents layout jump */
  min-height: 1.25em;
  margin-bottom: var(--sp-5);
  ${motion(css`animation: ${kFadeUp} 0.55s 0.22s cubic-bezier(0.22,1,0.36,1) both;`)}

  .Typewriter__cursor {
    color: var(--c-accent);              /* Warm Sand cursor */
    font-style: normal;
    font-weight: 400;
    ${motion(css`animation: ${kCursorBlink} 0.85s step-end infinite;`)}
  }

  /* Word colour classes — brand palette */
  .tw-teal    { color: var(--tw-1); }   /* Sage Forest   */
  .tw-saffron { color: var(--tw-2); }   /* Warm Sand     */
  .tw-rose    { color: var(--tw-3); }   /* Terracotta    */

  @media (max-width: 48em) {
    text-align: center;
    font-size: clamp(1.7rem, 6.5vw, 2.4rem);
  }
  @media (max-width: 30em) {
    font-size: clamp(1.5rem, 6vw, 2rem);
  }
`;
TypeLine.displayName = "TypeLine";

// ═══════════════════════════════════════════════════════════════════════════════
// DIVIDER — brand accent bar (replaces wave), no white-noise shimmer
// ═══════════════════════════════════════════════════════════════════════════════

export const TitleDivider = styled.div`
  position: relative;
  height: 3px;
  width: 48px;
  border-radius: var(--r-pill);
  /* Two-stop brand gradient: Sage Forest → Warm Sand */
  background: linear-gradient(90deg, var(--c-primary) 0%, var(--c-accent) 100%);
  margin-bottom: var(--sp-4);
  overflow: hidden;
  ${motion(css`animation: ${kUnderlineGrow} 0.65s 0.38s cubic-bezier(0.22,1,0.36,1) both;`)}

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255,255,255,0.6) 50%,
      transparent 100%
    );
    ${motion(css`animation: ${kShimmer} 3.5s 1s ease infinite;`)}
  }

  @media (max-width: 48em) {
    margin-left: auto;
    margin-right: auto;
  }
`;
TitleDivider.displayName = "TitleDivider";

// ═══════════════════════════════════════════════════════════════════════════════
// SUBTITLE
// ═══════════════════════════════════════════════════════════════════════════════

export const SubTitle = styled.p`
  font-family: "DM Sans", "Gill Sans", sans-serif;
  font-size: clamp(0.85rem, 1.3vw, 0.97rem);
  color: var(--c-ink-mid);
  font-weight: 400;
  line-height: 1.8;
  margin: 0 0 var(--sp-6);
  max-width: 420px;
  ${motion(css`animation: ${kFadeUp} 0.55s 0.30s cubic-bezier(0.22,1,0.36,1) both;`)}

  @media (max-width: 48em) {
    text-align: center;
    max-width: 100%;
    font-size: 0.9rem;
  }
`;
SubTitle.displayName = "SubTitle";

// ═══════════════════════════════════════════════════════════════════════════════
// CTA BUTTONS
// ═══════════════════════════════════════════════════════════════════════════════

export const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  margin-bottom: var(--sp-5);
  ${motion(css`animation: ${kFadeUp} 0.55s 0.38s cubic-bezier(0.22,1,0.36,1) both;`)}

  @media (max-width: 48em) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: var(--sp-3);
  }
`;
ButtonRow.displayName = "ButtonRow";

export const PrimaryBtn = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.78rem 1.85rem;
  border-radius: var(--r-pill);
  border: none;
  /* Sage Forest gradient — brand primary */
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%);
  color: ${COLORS.white};
  font-family: "DM Sans", sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.035em;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  min-height: 48px;
  box-shadow: 0 4px 18px ${COLORS.primaryGlow}, 0 1px 4px rgba(0,0,0,0.1);
  transition: transform var(--t), box-shadow var(--t);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 55%);
    opacity: 0;
    transition: opacity var(--t);
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
    transform: translateX(-110%);
  }
  svg {
    width: 13px; height: 13px; flex-shrink: 0;
    transition: transform var(--t);
  }
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px ${COLORS.primaryGlow}, 0 2px 8px rgba(0,0,0,0.12);
    &::before { opacity: 1; }
    &::after  { transition: transform 0.5s ease; transform: translateX(220%); }
    svg { transform: translateX(3px); }
  }
  &:active { transform: translateY(0) scale(0.98); }
  &:focus-visible { outline: 2.5px solid ${COLORS.primary}; outline-offset: 3px; }

  @media (max-width: 48em) { width: 100%; min-height: 50px; }
`;
PrimaryBtn.displayName = "PrimaryBtn";

export const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: var(--r-pill);
  border: 1.5px solid ${COLORS.border};
  background: ${COLORS.surface82};
  color: var(--c-ink-mid);
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  cursor: pointer;
  white-space: nowrap;
  min-height: 48px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: border-color var(--t), color var(--t), background var(--t),
              transform var(--t), box-shadow var(--t);

  svg {
    width: 13px; height: 13px; flex-shrink: 0;
    opacity: 0.65;
    transition: opacity var(--t), transform var(--t);
  }
  &:hover {
    border-color: ${COLORS.primary};
    color: ${COLORS.primaryDark};
    background: ${COLORS.bgLight};
    transform: translateY(-1px);
    box-shadow: var(--s-sm);
    svg { opacity: 1; transform: scale(1.08); }
  }
  &:active { transform: translateY(0) scale(0.98); }
  &:focus-visible { outline: 2.5px solid ${COLORS.primary}; outline-offset: 3px; }

  @media (max-width: 48em) { width: 100%; min-height: 50px; }
`;
GhostBtn.displayName = "GhostBtn";

// ═══════════════════════════════════════════════════════════════════════════════
// TRUST ROW
// ═══════════════════════════════════════════════════════════════════════════════

export const TrustRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-3) var(--sp-4);
  margin-bottom: var(--sp-6);
  ${motion(css`animation: ${kFadeUp} 0.55s 0.46s cubic-bezier(0.22,1,0.36,1) both;`)}

  @media (max-width: 48em) { justify-content: center; }
`;
TrustRow.displayName = "TrustRow";

export const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--c-ink-soft);
  letter-spacing: 0.01em;
  ${({ $delay }) => motion(css`
    animation: ${kTrustSlide} 0.42s ${$delay ?? 0}ms cubic-bezier(0.22,1,0.36,1) both;
  `)}

  .trust-icon {
    font-size: 0.65rem;
    color: var(--c-primary);
    opacity: 0.85;
    line-height: 1;
  }
`;
TrustItem.displayName = "TrustItem";

export const TrustSep = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--c-ink-soft);
  opacity: 0.28;
  flex-shrink: 0;
`;
TrustSep.displayName = "TrustSep";

// ═══════════════════════════════════════════════════════════════════════════════
// STAT CARDS — glass surface, brand accent on hover
// ═══════════════════════════════════════════════════════════════════════════════

export const StatsStrip = styled.div`
  display: flex;
  gap: var(--sp-3);
  flex-wrap: wrap;
  ${motion(css`animation: ${kFadeUp} 0.55s 0.54s cubic-bezier(0.22,1,0.36,1) both;`)}

  @media (max-width: 48em) {
    justify-content: center;
    width: 100%;
  }
`;
StatsStrip.displayName = "StatsStrip";

export const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--sp-3) var(--sp-5);
  border-radius: var(--r-lg);
  border: 1px solid var(--c-border);
  background: var(--c-surface-hi);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow: var(--s-xs), inset 0 1px 0 rgba(255,255,255,0.85);
  transition: border-color var(--t), transform var(--t), box-shadow var(--t);
  cursor: default;
  ${({ $delay }) => motion(css`
    animation: ${kStatPop} 0.42s ${$delay ?? 0}ms cubic-bezier(0.22,1,0.36,1) both;
  `)}

  &:hover {
    border-color: ${COLORS.borderGreen20};
    transform: translateY(-2px);
    box-shadow: var(--s), 0 0 18px ${COLORS.primaryGlow};
  }
`;
StatCard.displayName = "StatCard";

export const StatValue = styled.span`
  font-family: "Cormorant Garamond", "Playfair Display", Georgia, serif;
  font-size: clamp(1.2rem, 2.2vw, 1.55rem);
  font-weight: 700;
  line-height: 1;
  color: var(--c-primary-dark);          /* Deep Forest — strong, legible */
`;
StatValue.displayName = "StatValue";

export const StatLabel = styled.span`
  font-family: "DM Sans", sans-serif;
  font-size: 0.63rem;
  font-weight: 500;
  color: var(--c-ink-soft);
  letter-spacing: 0.07em;
  text-transform: uppercase;
`;
StatLabel.displayName = "StatLabel";

// Re-export keyframes used externally
export { kFadeUp, kFadeIn, kShimmer };